import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useDebounce } from '@/hooks/useDebounce';
import { useSearchPlaces } from '../../hooks/useVenueManagement';
import type { Place } from '../../types';

interface PlaceSearchProps {
  onPlaceSelect: (place: Place) => void;
  selectedPlace?: Place | null;
  showResults?: boolean;
  onToggleResults?: () => void;
}

export function PlaceSearch({ 
  onPlaceSelect, 
  selectedPlace,
  showResults = true,
  onToggleResults
}: PlaceSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 800);
  
  const { data, isLoading, error } = useSearchPlaces(debouncedQuery, debouncedQuery.length > 0 && showResults);

  return (
    <div className="space-y-4">
      {selectedPlace && !showResults ? (
        <>
          <Card className="p-4 bg-accent/50">
            <h4 className="font-medium">{selectedPlace.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedPlace.formatted_address}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Place ID: {selectedPlace.place_id}
            </p>
          </Card>
          <Button
            variant="outline"
            onClick={onToggleResults}
            className="w-full"
          >
            Change selection
          </Button>
        </>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search for a place (e.g., 'restaurant, NY', 'coffee shop', 'Starbucks')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {showResults && (
        <>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center text-sm text-destructive py-4">
              Failed to search places. Please try again.
            </div>
          )}

          {data && data.places.length === 0 && debouncedQuery.length > 0 && (
            <div className="text-center text-sm text-muted-foreground py-8">
              No places found for "{debouncedQuery}"
            </div>
          )}

          {data && data.places.length > 0 && (
            <div className="space-y-2">
              {data.places.map((place) => (
                <Card
                  key={place.place_id}
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => onPlaceSelect(place)}
                >
                  <h4 className="font-medium">{place.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {place.formatted_address}
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlaceSelect(place);
                    }}
                  >
                    Select this place
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}